import { motion } from "motion/react";
import { Helmet } from "react-helmet-async";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/src/components/ui/accordion";
import { FAQSchema } from "@/src/components/FAQSchema";

const faqs = [
  {
    question: "What’s the Gallup Strengthsfinder assessment all about?",
    answer: "It’s a strengths assessment which Prof. Donald Clifton developed and is used today to identify the areas that energise you as a person and where you are the most engaged. The assessment tests you on 34 strengths to determine what your 5 dominant strengths are."
  },
  {
    question: "Where did the Strengths idea come from?",
    answer: "Prof. Donald Clifton focussed on positive psychology because he realised that the way our world works is to focus on what’s wrong with us and not what’s right with us and he wanted to change that concept and hence the strengths idea was born!"
  },
  {
    question: "Why should I get coaching and not only do the Gallup Strengthsfinder assessment?",
    answer: "An assessment without coaching is like buying a new cell phone without using the instructions, yes you’ll figure out how to use the basics of the phone but you’ll never use the full potential of the phone."
  },
  {
    question: "What is the credibility of the Gallup Strengthsfinder assessment?",
    answer: "Please take a look at our resources section at the CSF Technical Report to find out exactly how credible the Strengthsfinder assessment is."
  },
  {
    question: "Is it a lot of homework after each session?",
    answer: "No, any busy person will be able to do it. You and the coach will conclude on the amount of time you have to do the homework."
  },
  {
    question: "If I’m not comfortable with the coach can I get a different coach?",
    answer: "Definitely! We are all different and don’t relate with every single person we meet and it is very import for you to be able to relate and be comfortable with your coach. Every coach knows it’s not personal."
  },
  {
    question: "If I don’t enjoy the process can I stop and get my money back?",
    answer: "We’ve never had a case where the individual doesn’t enjoy the coaching but if you don’t enjoy the coaching you are can definitely stop with the process but a refund won’t be possible because we pay our coaches upfront."
  },
  {
    question: "Do I have to have the Individual coaching before I can get the Executive coaching?",
    answer: "No not necessarily but we recommend that you first just focus on your top 5 strengths and then only move towards the Executive coaching. The Executive coaching entails a lot of information and can some times be overwhelming if not enough time by the individual was focussed on their top 5 strengths first."
  },
  {
    question: "Can I pay monthly or do I have to pay it all at once?",
    answer: "You can pay monthly or you can pay it all up front."
  },
  {
    question: "I’ve done a lot of personal assessments and coaching, what makes this process any different?",
    answer: "The detail of this process is what makes it different as well as the application of it. The strengths assessment gives a detail description of where your strengths lie and that’s the foundation of our coaching. Focus on your strengths and managing around your weaknesses."
  },
  {
    question: "If I’ve done other assessments can I share the information with you so that you can incorporate it in to our sessions?",
    answer: "Our coaches are only qualified as talent coaches so for that reason we won’t be able to incorporate some of the other assessments but most of our coaches don’t only use the strengths assessment in their coaching, they also use other assessments."
  },
  {
    question: "I’ve done my Gallup Strengthsfinder assessment more than a year ago, can you still coach me?",
    answer: "As long as you know what your top 5 strengths are we can always coach you, it doesn’t matter when you did the assessment."
  },
  {
    question: "Where does the coaching take place?",
    answer: "The individual and coach will decide on a place where both parties are comfortable for the coaching to take place."
  },
  {
    question: "Is coaching via Skype an option?",
    answer: "Yes definitely! Usually only the first session takes place personally and then the other sessions can take place via Skype, it all depends on the individual."
  }
];

export function FAQ() {
  return (
    <div className="pt-24 pb-16 min-h-screen">
      <Helmet>
        <title>Strengthsfinder FAQ | Iwantcoaching Pretoria</title>
        <meta name="description" content="Find answers to common questions about Gallup Strengthsfinder assessment and coaching. Learn about the origins and benefits of strengths-based coaching." />
        <meta name="geo.region" content="ZA-GP" />
        <meta name="geo.placename" content="Pretoria" />
        <link rel="canonical" href="https://iwantcoaching.co.za/faq" />
      </Helmet>
      <FAQSchema />
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl font-serif mb-6">FAQ's</h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Find answers to common questions about Strengthsfinder and our coaching process.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border border-primary/10 rounded-2xl px-6 bg-background overflow-hidden"
                >
                  <AccordionTrigger className="text-left font-serif text-lg py-6 hover:text-primary transition-colors hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
